import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styles from './page.module.css';
import { formatarMoeda, UNIDADE_PADRAO } from '@tera/shared/src/types/Catalogo';

type Estado =
    | 'carregando'
    | 'enviado'
    | 'aceito'
    | 'recusado'
    | 'cancelado'
    | 'expirado'
    | 'invalido'
    | 'erro';

type ItemPublico = {
    nome: string;
    unidade?: string | null;
    quantidade: number;
    preco_unitario: number;
    subtotal: number;
};

type OrcamentoPublico = {
    moeda: string;
    subtotal: number;
    desconto_valor: number;
    desconto_descricao?: string | null;
    total: number;
    valido_ate?: string | null;
    itens: ItemPublico[];
};

/**
 * Página pública do orçamento. Só exibe números que o servidor calculou e
 * gravou no momento da emissão — nada é recalculado aqui.
 */
const OrcamentoPublicoPage = () => {
    const { token } = useParams<{ token: string }>();
    const [estado, setEstado] = useState<Estado>('carregando');
    const [orcamento, setOrcamento] = useState<OrcamentoPublico | null>(null);
    const [aceitando, setAceitando] = useState(false);
    const [voltarUrl, setVoltarUrl] = useState<string | null>(null);

    useEffect(() => {
        let cancelado = false;
        (async () => {
            try {
                const res = await axios.get(`/api/orcamento-publico/${token}`);
                if (cancelado) return;
                setOrcamento(res.data.orcamento);
                setEstado(res.data.estado);
            } catch (err: any) {
                if (cancelado) return;
                setEstado(err.response?.status === 404 ? 'invalido' : 'erro');
            }
        })();
        return () => {
            cancelado = true;
        };
    }, [token]);

    const aceitar = async () => {
        setAceitando(true);
        try {
            const res = await axios.post(`/api/orcamento-publico/${token}/aceitar`);
            setVoltarUrl(res.data?.voltarUrl ?? null);
            setEstado('aceito');
        } catch (err: any) {
            // O servidor devolve o estado real (já aceito por outra aba, vencido...).
            const estadoServidor = err.response?.data?.estado;
            setEstado(estadoServidor ?? 'erro');
        } finally {
            setAceitando(false);
        }
    };

    const renderMensagem = (titulo: string, texto: string) => (
        <div className={styles.card}>
            <div className={styles.centro}>
                <h1 className={styles.titulo}>{titulo}</h1>
                <p className={styles.descricao}>
                    {texto.split('\n').map((linha, i) => (
                        <div key={i}>{linha}</div>
                    ))}
                </p>
                {voltarUrl && (
                    <a className={styles.voltarLink} href={voltarUrl}>
                        Voltar à conversa
                    </a>
                )}
            </div>
        </div>
    );

    const renderOrcamento = (o: OrcamentoPublico, podeAceitar: boolean) => (
        <div className={styles.card}>
            <h1 className={styles.titulo}>Seu orçamento</h1>

            <table className={styles.tabela}>
                <thead>
                    <tr>
                        <th>Item</th>
                        <th className={styles.numero}>Qtd.</th>
                        <th className={styles.numero}>Unitário</th>
                        <th className={styles.numero}>Subtotal</th>
                    </tr>
                </thead>
                <tbody>
                    {o.itens.map((item, i) => (
                        <tr key={i}>
                            <td>{item.nome}</td>
                            <td className={styles.numero}>
                                {item.quantidade} {item.unidade || UNIDADE_PADRAO}
                            </td>
                            <td className={styles.numero}>
                                {formatarMoeda(item.preco_unitario, o.moeda)}
                            </td>
                            <td className={styles.numero}>{formatarMoeda(item.subtotal, o.moeda)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className={styles.totais}>
                <div className={styles.linhaTotal}>
                    <span>Subtotal</span>
                    <span>{formatarMoeda(o.subtotal, o.moeda)}</span>
                </div>
                {o.desconto_valor > 0 && (
                    <div className={styles.linhaTotal}>
                        <span>{o.desconto_descricao ?? 'Desconto'}</span>
                        <span>-{formatarMoeda(o.desconto_valor, o.moeda)}</span>
                    </div>
                )}
                <div className={`${styles.linhaTotal} ${styles.totalFinal}`}>
                    <span>Total</span>
                    <span>{formatarMoeda(o.total, o.moeda)}</span>
                </div>
            </div>

            {o.valido_ate && (
                <p className={styles.descricao}>
                    Válido até {new Date(o.valido_ate).toLocaleDateString('pt-BR')}
                </p>
            )}

            {podeAceitar && (
                <button className={styles.botao} type="button" onClick={aceitar} disabled={aceitando}>
                    {aceitando ? 'Confirmando...' : 'Aceitar orçamento'}
                </button>
            )}
        </div>
    );

    let conteudo: React.ReactNode;
    if (estado === 'carregando') {
        conteudo = renderMensagem('Carregando...', 'Aguarde um instante.');
    } else if (estado === 'invalido') {
        conteudo = renderMensagem('Orçamento não encontrado', 'Este link é inválido.');
    } else if (estado === 'erro') {
        conteudo = renderMensagem('Ops!', 'Ocorreu um erro. Tente novamente mais tarde.');
    } else if (estado === 'aceito') {
        conteudo = renderMensagem(
            'Orçamento aceito!',
            'Recebemos sua confirmação.\nVocê já pode fechar esta janela e voltar à conversa.',
        );
    } else if (estado === 'recusado') {
        conteudo = renderMensagem('Orçamento recusado', 'Este orçamento foi recusado.');
    } else if (estado === 'cancelado') {
        conteudo = renderMensagem('Orçamento cancelado', 'Este orçamento foi cancelado.');
    } else if (estado === 'expirado' && orcamento) {
        conteudo = (
            <>
                {renderOrcamento(orcamento, false)}
                {renderMensagem('Orçamento vencido', 'O prazo de validade deste orçamento terminou.')}
            </>
        );
    } else if (estado === 'expirado') {
        conteudo = renderMensagem('Orçamento vencido', 'O prazo de validade deste orçamento terminou.');
    } else if (orcamento) {
        conteudo = renderOrcamento(orcamento, true);
    }

    return <div className={styles.wrapper}>{conteudo}</div>;
};

export default OrcamentoPublicoPage;
